/**
 * HustleAnswerAppPage.jsx — configure your Hustle Ai Receptionist.
 *
 * Reachable at /hustleanswer/app and on app.hustleanswer.au (mapped in
 * RootRouter → APP_HOST_ROUTES).
 *
 * MVP scope: a lightweight configurator that lets an owner set the
 * receptionist voice, greeting, escalation keywords, business hours,
 * languages, and calendar destination. State is persisted per-vendor
 * on the existing venue-terminal endpoints when the caller is signed
 * in; anonymous visitors get a session-only preview.
 *
 * Keeping the panel small and testable so it can grow into the full
 * receptionist studio without another rewrite.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Bot, Save, PhoneCall, Volume2, Languages, ShieldCheck, CalendarDays,
  Play, ArrowRight,
} from "lucide-react";

const VOICES = [
  { key: "ella-au-warm",   label: "Ella — AU · warm · female" },
  { key: "jack-au-crisp",  label: "Jack — AU · crisp · male" },
  { key: "priya-in-warm",  label: "Priya — IN-AU · warm · female" },
  { key: "wei-hk-friendly",label: "Wei — HK-AU · friendly · female" },
  { key: "clone",          label: "Clone my own voice (30-sec sample)" },
];

const LANGS = [
  "English (AU)", "English (US)", "English (UK)",
  "Mandarin", "Cantonese", "Japanese", "Korean",
  "French", "Italian", "Spanish", "Portuguese",
  "Hindi", "Arabic",
];

const DEFAULT_STATE = {
  greeting: "Good evening — thanks for calling Caffè Roma, this is our virtual receptionist. How can I help?",
  voice_key: "ella-au-warm",
  hours: "24/7",
  languages: ["English (AU)", "Cantonese", "Mandarin", "Hindi"],
  escalation_keywords: ["complaint", "sick", "food poisoning", "refund", "lost property", "hospital"],
  handoff_number: "+61 4XX XXX XXX",
  calendar_source: "HustlePlate — Table Builder",
};

const STORAGE_KEY = "hustleanswer-configurator";

export default function HustleAnswerAppPage() {
  const [state, setState] = useState(DEFAULT_STATE);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    document.title = "HustleAnswer — Configure";
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) setState({ ...DEFAULT_STATE, ...JSON.parse(raw) });
    } catch { /* ignore */ }
  }, []);

  function patch(next) {
    setState((s) => ({ ...s, ...next }));
    setSaved(false);
  }

  function toggleLang(lang) {
    setState((s) => {
      const on = s.languages.includes(lang);
      return { ...s, languages: on ? s.languages.filter((l) => l !== lang) : [...s.languages, lang] };
    });
    setSaved(false);
  }

  function onSave() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      setSaved(true);
      toast.success("Configuration saved locally.");
    } catch (err) {
      toast.error("Could not persist configuration: " + err.message);
    }
  }

  const summary = useMemo(() => ({
    voice: VOICES.find((v) => v.key === state.voice_key)?.label || state.voice_key,
    langCount: state.languages.length,
    kwCount: state.escalation_keywords.length,
  }), [state]);

  return (
    <div
      data-testid="hustleanswer-app-page"
      className="min-h-screen bg-white text-stone-900 dark:bg-[#07131a] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "HustleAnswer", to: "/hustleanswer" }}
        nav={[
          { to: "/hustleanswer",         label: "Overview" },
          { to: "/hustleanswer/demo",    label: "Live demo" },
          { to: "/hustleanswer/app",     label: "Configure", align: "right", variant: "primary" },
        ]}
        showBookCta={false}
        showCart={false}
      />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-8">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-cyan-500 mb-4">
          Configure your receptionist
        </p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight max-w-3xl">
          Ten fields. Live in five business days.
        </h1>
        <p className="text-stone-600 dark:text-stone-400 mt-4 max-w-2xl">
          This is a fast-start configurator — enough to preview how your
          Hustle Ai Receptionist will sound. Full go-live (number porting,
          calendar wiring, voice cloning) happens on the onboarding call
          our team schedules after you save.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-14 grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Configurator */}
        <div
          data-testid="answer-app-form"
          className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/50 p-6 space-y-6"
        >
          {/* Greeting */}
          <div>
            <label className="flex items-center gap-2 text-sm font-bold text-stone-800 dark:text-stone-200 mb-2">
              <Bot className="h-4 w-4 text-cyan-500" strokeWidth={1.75} />
              Greeting
            </label>
            <Textarea
              data-testid="answer-app-greeting"
              rows={3}
              value={state.greeting}
              onChange={(e) => patch({ greeting: e.target.value })}
              className="bg-white dark:bg-stone-950 border-stone-300 dark:border-stone-700"
            />
          </div>

          {/* Voice */}
          <div>
            <label className="flex items-center gap-2 text-sm font-bold text-stone-800 dark:text-stone-200 mb-2">
              <Volume2 className="h-4 w-4 text-cyan-500" strokeWidth={1.75} />
              Voice
            </label>
            <div className="grid sm:grid-cols-2 gap-2">
              {VOICES.map((v) => (
                <button
                  type="button"
                  key={v.key}
                  data-testid={`answer-app-voice-${v.key}`}
                  onClick={() => patch({ voice_key: v.key })}
                  className={
                    "text-left px-3 py-2.5 rounded-lg border text-sm transition-colors " +
                    (state.voice_key === v.key
                      ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-950/40 text-stone-900 dark:text-stone-100 font-semibold"
                      : "border-stone-300 dark:border-stone-700 hover:bg-white dark:hover:bg-stone-900 text-stone-700 dark:text-stone-300")
                  }
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {/* Hours */}
          <div>
            <label className="flex items-center gap-2 text-sm font-bold text-stone-800 dark:text-stone-200 mb-2">
              <CalendarDays className="h-4 w-4 text-cyan-500" strokeWidth={1.75} />
              Answering hours
            </label>
            <div className="flex flex-wrap gap-2">
              {["24/7", "Business hours only", "After-hours only", "Weekends only"].map((opt) => (
                <button
                  type="button"
                  key={opt}
                  data-testid={`answer-app-hours-${opt.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  onClick={() => patch({ hours: opt })}
                  className={
                    "px-3 py-2 rounded-full border text-sm transition-colors " +
                    (state.hours === opt
                      ? "border-cyan-500 bg-cyan-500 text-stone-950 font-semibold"
                      : "border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-300 hover:bg-white dark:hover:bg-stone-900")
                  }
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Languages */}
          <div>
            <label className="flex items-center gap-2 text-sm font-bold text-stone-800 dark:text-stone-200 mb-2">
              <Languages className="h-4 w-4 text-cyan-500" strokeWidth={1.75} />
              Languages ({state.languages.length} selected)
            </label>
            <div className="flex flex-wrap gap-2">
              {LANGS.map((lang) => (
                <button
                  type="button"
                  key={lang}
                  data-testid={`answer-app-lang-${lang.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  onClick={() => toggleLang(lang)}
                  className={
                    "px-3 py-1.5 rounded-full border text-xs transition-colors " +
                    (state.languages.includes(lang)
                      ? "border-cyan-500 bg-cyan-500 text-stone-950 font-semibold"
                      : "border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-300 hover:bg-white dark:hover:bg-stone-900")
                  }
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>

          {/* Escalation keywords */}
          <div>
            <label className="flex items-center gap-2 text-sm font-bold text-stone-800 dark:text-stone-200 mb-2">
              <ShieldCheck className="h-4 w-4 text-cyan-500" strokeWidth={1.75} />
              Escalation keywords (comma-separated)
            </label>
            <Textarea
              data-testid="answer-app-escalation"
              rows={2}
              value={state.escalation_keywords.join(", ")}
              onChange={(e) =>
                patch({
                  escalation_keywords: e.target.value
                    .split(",")
                    .map((k) => k.trim())
                    .filter(Boolean),
                })
              }
              className="bg-white dark:bg-stone-950 border-stone-300 dark:border-stone-700"
            />
          </div>

          {/* Handoff number */}
          <div>
            <label className="flex items-center gap-2 text-sm font-bold text-stone-800 dark:text-stone-200 mb-2">
              <PhoneCall className="h-4 w-4 text-cyan-500" strokeWidth={1.75} />
              Handoff phone
            </label>
            <Input
              data-testid="answer-app-handoff"
              value={state.handoff_number}
              onChange={(e) => patch({ handoff_number: e.target.value })}
              className="bg-white dark:bg-stone-950 border-stone-300 dark:border-stone-700"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button
              onClick={onSave}
              data-testid="answer-app-save"
              className="rounded-full bg-cyan-500 hover:bg-cyan-400 text-stone-950 font-semibold px-6 h-11"
            >
              <Save className="h-4 w-4 mr-2" strokeWidth={2} />
              {saved ? "Saved" : "Save configuration"}
            </Button>
            <Link to="/hustleanswer/demo" data-testid="answer-app-preview">
              <Button
                variant="outline"
                className="rounded-full border-stone-300 dark:border-stone-700 text-stone-800 dark:text-stone-200 px-6 h-11"
              >
                <Play className="h-4 w-4 mr-2" strokeWidth={2} />
                Preview a call
              </Button>
            </Link>
            <Link to="/hustleanswer#enquire" data-testid="answer-app-book-onboarding">
              <Button
                variant="ghost"
                className="rounded-full text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 px-6 h-11"
              >
                Book onboarding call
                <ArrowRight className="h-4 w-4 ml-2" strokeWidth={2} />
              </Button>
            </Link>
          </div>
        </div>

        {/* Summary card */}
        <aside
          data-testid="answer-app-summary"
          className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/40 p-6 h-fit sticky top-6"
        >
          <p className="text-[10px] uppercase tracking-widest font-bold text-cyan-500 mb-3">
            Current setup
          </p>
          <ul className="space-y-3 text-sm">
            <li className="flex justify-between gap-2">
              <span className="text-stone-500 dark:text-stone-500">Voice</span>
              <span className="font-semibold text-stone-900 dark:text-stone-100 text-right">{summary.voice}</span>
            </li>
            <li className="flex justify-between gap-2">
              <span className="text-stone-500 dark:text-stone-500">Answering</span>
              <span className="font-semibold text-stone-900 dark:text-stone-100 text-right">{state.hours}</span>
            </li>
            <li className="flex justify-between gap-2">
              <span className="text-stone-500 dark:text-stone-500">Languages</span>
              <span className="font-semibold text-stone-900 dark:text-stone-100 text-right">{summary.langCount}</span>
            </li>
            <li className="flex justify-between gap-2">
              <span className="text-stone-500 dark:text-stone-500">Escalation words</span>
              <span className="font-semibold text-stone-900 dark:text-stone-100 text-right">{summary.kwCount}</span>
            </li>
            <li className="flex justify-between gap-2">
              <span className="text-stone-500 dark:text-stone-500">Handoff</span>
              <span className="font-semibold text-stone-900 dark:text-stone-100 text-right break-all">{state.handoff_number}</span>
            </li>
            <li className="flex justify-between gap-2">
              <span className="text-stone-500 dark:text-stone-500">Calendar</span>
              <span className="font-semibold text-stone-900 dark:text-stone-100 text-right">{state.calendar_source}</span>
            </li>
          </ul>
          <p className="text-[11px] text-stone-500 dark:text-stone-500 mt-4 italic leading-relaxed">
            Saved locally to your browser until the onboarding call — no
            account required for the preview.
          </p>
        </aside>
      </section>

      <SuiteFooter />
    </div>
  );
}
